import { supabase } from './supabase'

const INACTIVITY_TIMEOUT = 60 * 60 * 1000 // 60 minutes in milliseconds
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000 // 5 minutes in milliseconds

type ActivityCallback = () => void
type WarningCallback = () => void

export class ActivityTracker {
  private timeoutId: ReturnType<typeof setTimeout> | null
  private warningTimeoutId: ReturnType<typeof setTimeout> | null
  private isWarningShown: boolean
  private onTimeout: ActivityCallback
  private onWarning: WarningCallback
  private boundResetTimer: () => void

  constructor(onTimeout: ActivityCallback, onWarning: WarningCallback) {
    this.timeoutId = null
    this.warningTimeoutId = null
    this.isWarningShown = false
    this.onTimeout = onTimeout
    this.onWarning = onWarning
    this.boundResetTimer = this.resetTimer.bind(this)
  }

  public start(): void {
    // Add event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']
    events.forEach(event => {
      window.addEventListener(event, this.boundResetTimer, false)
    })

    // Start the initial timer
    this.resetTimer()
  }

  public stop(): void {
    // Remove event listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']
    events.forEach(event => {
      window.removeEventListener(event, this.boundResetTimer, false)
    })

    // Clear any existing timers
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId)
      this.warningTimeoutId = null
    }
  }

  public resetTimer(): void {
    // Clear any existing timers
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId)
    }

    // Reset warning state
    this.isWarningShown = false

    // Set warning timeout
    this.warningTimeoutId = setTimeout(() => {
      if (!this.isWarningShown) {
        this.isWarningShown = true
        this.onWarning()
      }
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT)

    // Set inactivity timeout
    this.timeoutId = setTimeout(async () => {
      // Sign out the user
      await supabase.auth.signOut()
      this.onTimeout()
    }, INACTIVITY_TIMEOUT)
  }

  public extendSession(): void {
    this.resetTimer()
  }
}
